import { FC } from 'react'

import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { Form, useParams } from '@remix-run/react'
import { z } from 'zod'

import { Accordion, MultiSelect, PrimaryButton } from '~/components'
import {
  AreasMultiSelectForm,
  areasMultiSelectFormSchema,
  CheckBoxOptions,
  FacilitiesAndServicesSection,
  facilitiesSchema,
  keywordsSchema,
  KeywordsSection,
  monthlyCostsFormSchema,
  MonthlyCostsSection,
  NumberOfUsersMultiSelectForm,
  numberOfUsersMultiSelectFormSchema,
  OfficeTypeMultiSelectForm,
  officeTypeMultiSelectFormSchema,
  useAreasForm,
  useFacilitiesForm,
  useKeywordForm,
  useMonthlyCostsForm,
  useNumberOfUsersForm,
  useOfficeTypeForm,
} from '~/features/common'

type Options = React.ComponentProps<typeof MultiSelect>['options']

export const searchFormSchema = z
  .object({})
  .merge(keywordsSchema)
  .merge(areasMultiSelectFormSchema)
  .merge(officeTypeMultiSelectFormSchema)
  .merge(numberOfUsersMultiSelectFormSchema)
  .merge(monthlyCostsFormSchema)
  .merge(facilitiesSchema)

type SearchFormProps = {
  className?: string
  areaFormOptions: Options
  officeTypeFormOptions: Options
  facilitiesAndServicesFormOptions: CheckBoxOptions
}

export const SearchForm: FC<SearchFormProps> = ({
  className,
  areaFormOptions,
  officeTypeFormOptions,
  facilitiesAndServicesFormOptions,
}) => {
  const { prefectureSlug } = useParams()
  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: searchFormSchema })
    },
  })

  const {
    value: areasValue,
    onChange: onChangeAreas,
    name: areasInputName,
  } = useAreasForm(fields.areas)
  const {
    value: officeTypesValue,
    onChange: onChangeOfficeTypes,
    name: officeTypeInputName,
  } = useOfficeTypeForm(fields.officeTypes)
  const {
    value: minPeopleValue,
    onChange: onChangeMinPeople,
    name: minPeopleInputName,
  } = useNumberOfUsersForm(fields.minPeople)
  const {
    minMonthlyRentValue,
    maxMonthlyRentValue,
    onChangeMinMonthlyRent,
    onChangeMaxMonthlyRent,
    minMonthlyRentName,
    maxMonthlyRentName,
  } = useMonthlyCostsForm(fields.minMonthlyRent, fields.maxMonthlyRent)
  const {
    value: facilitiesValue,
    onChange: onChangeFacilities,
    name: facilitiesInputName,
  } = useFacilitiesForm(fields.facilities)
  const {
    value: keywordValue,
    onChange: onChangeKeyword,
    name: keywordInputName,
  } = useKeywordForm(fields.keyword)

  return (
    <FormProvider context={form.context}>
      <Form className={className} method="post" {...getFormProps(form)}>
        <input name="prefectureSlug" type="hidden" value={prefectureSlug} />
        <div className="mb-4 grid grid-cols-1 gap-2 lg:grid-cols-3">
          <AreasMultiSelectForm
            name={areasInputName}
            onChange={onChangeAreas}
            options={areaFormOptions}
            value={areasValue}
          />
          <OfficeTypeMultiSelectForm
            name={officeTypeInputName}
            onChange={onChangeOfficeTypes}
            options={officeTypeFormOptions}
            value={officeTypesValue}
          />
          <NumberOfUsersMultiSelectForm
            name={minPeopleInputName}
            onChange={onChangeMinPeople}
            value={minPeopleValue}
          />
        </div>
        <Accordion
          contentClassName="ml-[calc(50%-50vw)] mt-4 w-screen md:ml-0 md:w-full"
          triggerText={{
            close: 'その他の条件を指定',
            open: 'その他の条件を閉じる',
          }}
          wrapperClassName="mb-4"
        >
          <MonthlyCostsSection
            maxMonthlyRentName={maxMonthlyRentName}
            maxMonthlyRentValue={maxMonthlyRentValue}
            maxValueErrorMessages={fields.maxMonthlyRent.errors}
            minMonthlyRentName={minMonthlyRentName}
            minMonthlyRentValue={minMonthlyRentValue}
            minValueErrorMessages={fields.minMonthlyRent.errors}
            onChangeMaxMonthlyRent={onChangeMaxMonthlyRent}
            onChangeMinMonthlyRent={onChangeMinMonthlyRent}
          />
          <FacilitiesAndServicesSection
            meta={fields.facilities}
            name={facilitiesInputName}
            onChange={onChangeFacilities}
            options={facilitiesAndServicesFormOptions}
            value={facilitiesValue}
          />
          <KeywordsSection
            errorMessages={fields.keyword.errors}
            name={keywordInputName}
            onChange={onChangeKeyword}
            value={keywordValue}
          />
        </Accordion>
        <PrimaryButton
          className="mx-auto block w-full max-w-[342px]"
          color="brand"
        >
          検索する
        </PrimaryButton>
      </Form>
    </FormProvider>
  )
}
